/**
 * とうろく・ログインの状態(docs/10_paltown_platform.md §3.1)。
 *
 * `gameStore` とは分けてある — **ログインしなくてもタイピングは今までどおり遊べる**ので、
 * ゲーム側のどこもこのストアに依存させない(ここが落ちても遊べる)。
 *
 * 画面の分岐:
 *
 *   [とうろく] メール+パスワード ─→ 匿名なら link(同一UID・進捗そのまま)
 *                                └─ もう使われているメール → 「ログイン」タブへ誘導
 *   [とうろく] Google           ─→ 匿名なら linkWithPopup(同一UID)
 *                                ├─ ポップアップ不可 → redirect(戻ってきたら completeRedirect)
 *                                └─ そのGoogleが既存ユーザー → `conflict` を立てて確認
 *                                     [ログインする] → いまの きろくは ひきつがれない
 *   [ログイン] メール+パスワード ─→ signIn(いまの きろくは ひきつがれない)
 */
import { create } from 'zustand';
import { appServices } from '../services/appServices';
import {
  authErrorMessage,
  AuthError,
  type AccountUser,
  type AuthOutcome,
  type AuthService,
} from '../services/auth';

/** 登録できたか / 何を出すか。 */
export interface AuthState {
  /** ローカルモードなら false = 導線ごと出さない。 */
  readonly supported: boolean;
  readonly user: AccountUser | null;
  /** 通信中(ボタンを二度押しさせない)。 */
  readonly busy: boolean;
  readonly error: string | null;
  /** 「そのアカウントは既に使われています」の確認を出すか。 */
  readonly conflict: boolean;
  /** 「ログイン」タブへ切り替えてほしい(メール重複時)。 */
  readonly suggestSignIn: boolean;

  init(): void;
  register(email: string, password: string): Promise<void>;
  signIn(email: string, password: string): Promise<void>;
  signInWithGoogle(): Promise<void>;
  /** 衝突時に「そっちのアカウントで入る」を選んだ(匿名ぶんは引き継がれない)。 */
  useExistingAccount(): Promise<void>;
  dismissConflict(): void;
  signOut(): Promise<void>;
  clearError(): void;
  clearSuggestSignIn(): void;
}

export function createAuthStore(auth: AuthService) {
  return create<AuthState>()((set, get) => {
    let started = false;

    /** 共通の実行枠: 二重実行を防ぎ、失敗を画面用の文言に変換する。 */
    const run = async (action: () => Promise<AuthOutcome | void>): Promise<void> => {
      if (get().busy) return;
      set({ busy: true, error: null, suggestSignIn: false });
      try {
        await action();
        set({ busy: false, conflict: false });
      } catch (error) {
        const code = error instanceof AuthError ? error.code : 'unknown';
        set({
          busy: false,
          // ポップアップを自分で閉じただけならエラー扱いしない(驚かせない)。
          error: code === 'popup-cancelled' ? null : authErrorMessage(error),
          conflict: code === 'account-exists',
          suggestSignIn: code === 'email-already-in-use',
        });
      }
    };

    return {
      supported: auth.supported,
      user: auth.current(),
      busy: false,
      error: null,
      conflict: false,
      suggestSignIn: false,

      init() {
        if (started || !auth.supported) return;
        started = true;
        auth.subscribe((user) => set({ user }));
        // リダイレクト認証から戻ってきた場合の続き(何も無ければ null)。
        void run(async () => {
          await auth.completeRedirect();
        });
      },

      async register(email, password) {
        await run(() => auth.registerWithEmail(email.trim(), password));
      },

      async signIn(email, password) {
        await run(() => auth.signInWithEmail(email.trim(), password));
      },

      async signInWithGoogle() {
        await run(() => auth.signInWithGoogle());
      },

      async useExistingAccount() {
        await run(() => auth.continueWithExistingAccount());
      },

      dismissConflict() {
        auth.discardExistingAccount();
        set({ conflict: false, error: null });
      },

      async signOut() {
        await run(() => auth.signOut());
      },

      clearError() {
        set({ error: null });
      },

      clearSuggestSignIn() {
        set({ suggestSignIn: false });
      },
    };
  });
}

export const useAuthStore = createAuthStore(appServices.auth);
